import { useEffect, useRef } from 'react'

type RGBColor = [number, number, number]

// Same colors as the Skia version but in RGB arrays
const colorsRgb: RGBColor[] = [
  [0, 255, 208], // 'rgba(0, 255, 208, 1)'
  [0, 128, 255], // 'rgba(0,128,255,1)'
  [128, 0, 255], // 'rgba(128,0,255,1)'
  [255, 0, 255], // 'rgba(255,0,255,1)'
  [255, 0, 128], // 'rgba(255,0,128,1)'
  [255, 128, 0], // 'rgba(255,128,0,1)'
  [255, 213, 0], // 'rgba(255, 213, 0, 1)'
]

// Function to interpolate between two RGB colors
const lerpColor = (color1: RGBColor, color2: RGBColor, t: number): RGBColor => [
  Math.round(color1[0] + (color2[0] - color1[0]) * t),
  Math.round(color1[1] + (color2[1] - color1[1]) * t),
  Math.round(color1[2] + (color2[2] - color1[2]) * t),
]

type MagicGlowCanvasProps = {
  borderRadius: number
  width: number
  height: number
  fillWidth?: number
  duration?: number
  opacity?: number
  blur?: number
  numSegments?: number
}

const MagicGlowCanvas = ({
  borderRadius,
  fillWidth = 25,
  duration = 5000,
  opacity = 0.66,
  blur = 20,
  numSegments = 180,
  width,
  height,
}: MagicGlowCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!canvasRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = width
    canvas.height = height

    // Pre-calculate the mask once
    const maskCanvas = document.createElement('canvas')
    maskCanvas.width = width
    maskCanvas.height = height
    const maskCtx = maskCanvas.getContext('2d')

    if (maskCtx) {
      // Create the gradient mask
      maskCtx.fillStyle = 'white'
      maskCtx.fillRect(0, 0, width, height)

      maskCtx.save()
      maskCtx.globalCompositeOperation = 'destination-out'

      // Create multiple layers for smooth transition
      const steps = 20
      for (let i = 0; i < steps; i++) {
        const progress = i / steps
        const currentEdge = fillWidth * progress
        const alpha = progress

        maskCtx.fillStyle = `rgba(0, 0, 0, ${alpha * 0.8})`
        maskCtx.beginPath()
        maskCtx.roundRect(
          currentEdge,
          currentEdge,
          width - currentEdge * 2,
          height - currentEdge * 2,
          Math.max(0, borderRadius - currentEdge)
        )
        maskCtx.fill()
      }

      maskCtx.restore()
    }

    const startTime = performance.now()

    const animate = (currentTime: number = performance.now()) => {
      const elapsed =
        currentTime > startTime ? (currentTime - startTime) % duration : 0
      const progress = elapsed / duration

      // Clear canvas
      ctx.clearRect(0, 0, width, height)

      const centerX = width / 2
      const centerY = height / 2
      const maxRadius = Math.max(width, height)

      const totalColors = colorsRgb.length

      // Draw the sweep gradient first (full area)
      // Smooth color shifting with interpolation
      const colorProgress = progress * totalColors
      const colorOffset = Math.floor(colorProgress)
      const lerpAmount = colorProgress - colorOffset

      // Create 3-color window that shifts through the palette
      const getColor = (offset: number) =>
        colorsRgb[(colorOffset + offset) % totalColors]

      const currentSet: RGBColor[] = [getColor(0), getColor(1), getColor(2)]
      const nextSet: RGBColor[] = [getColor(1), getColor(2), getColor(3)]

      // Interpolate between current and next color sets, then duplicate first for seamless loop
      const interpolatedColors = currentSet.map((current, i) =>
        lerpColor(current, nextSet[i], lerpAmount)
      )
      const currentColors = [...interpolatedColors, interpolatedColors[0]]

      for (let i = 0; i < numSegments; i++) {
        const angle = (i / numSegments) * 2 * Math.PI + progress * 2 * Math.PI
        const nextAngle =
          ((i + 1) / numSegments) * 2 * Math.PI + progress * 2 * Math.PI

        // Interpolate between the 4 current colors (3 + duplicate first)
        const segmentProgress = i / numSegments
        const colorPosition = segmentProgress * 3 // Map to 0-3 range for 4 colors

        // Determine which two colors to interpolate between
        const colorIndex = Math.floor(colorPosition)
        const t = colorPosition - colorIndex

        const finalColor = lerpColor(
          currentColors[colorIndex],
          currentColors[colorIndex + 1],
          t
        )

        ctx.fillStyle = `rgba(${finalColor[0]}, ${finalColor[1]}, ${finalColor[2]}, ${opacity})`

        // Draw segment
        ctx.beginPath()
        ctx.moveTo(centerX, centerY)
        ctx.arc(centerX, centerY, maxRadius, angle, nextAngle)
        ctx.closePath()
        ctx.fill()
      }

      // Apply outer border clipping
      ctx.save()
      ctx.globalCompositeOperation = 'destination-in'
      ctx.beginPath()
      ctx.roundRect(0, 0, width, height, borderRadius)
      ctx.fill()
      ctx.restore()

      // Apply the pre-calculated mask
      if (maskCanvas && maskCanvas.height > 0) {
        ctx.save()
        ctx.globalCompositeOperation = 'destination-in'
        ctx.drawImage(maskCanvas, 0, 0)
        ctx.restore()
      }

      // Add overall blur effect for the glow
      const blurCanvas = document.createElement('canvas')
      blurCanvas.width = width
      blurCanvas.height = height
      const blurCtx = blurCanvas.getContext('2d')

      if (blurCtx && canvas.height > 0) {
        blurCtx.filter = `blur(${blur}px)`
        blurCtx.drawImage(canvas, 0, 0)

        // Composite the blurred version with reduced opacity
        ctx.save()
        ctx.globalAlpha = 1
        ctx.globalCompositeOperation = 'screen'
        ctx.drawImage(blurCanvas, 0, 0)
        ctx.restore()
      }

      animationRef.current = requestAnimationFrame(animate)
    }

    requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [
    width,
    height,
    borderRadius,
    fillWidth,
    duration,
    opacity,
    blur,
    numSegments,
  ])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  )
}

export default MagicGlowCanvas
